import { createPortal } from 'react-dom'
import { Printer, Plus } from 'lucide-react'
import { formatNaira } from '../../utils/formatters'

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between py-2 border-b border-gray-100 last:border-0 ${bold ? 'font-bold' : ''}`}>
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm ${bold ? 'text-gray-900' : 'text-gray-800'}`}>{value}</span>
    </div>
  )
}

const STATUS_LABELS = {
  unpaid: 'UNPAID — CREDIT',
  batched: 'IN WEEKLY BATCH',
  reconciled: 'RECONCILED',
  paid: 'PAID',
}

// formatNaira rounds to whole naira, which is fine for totals but breaks a
// printed receipt: a ₦1,187.50 pump price would print as ₦1,188 and the
// customer's own litres × price sum would not match the total. Kobo are kept
// here so the slip reconciles in the hand.
const nairaPrecise = (amount) =>
  `₦${Number(amount).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

function PrintRow({ label, value }) {
  return (
    <div className="rcpt-row">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  )
}

// The copy that actually reaches the paper. Portalled to document.body so no
// ancestor layout or positioning can affect it, and styled entirely by the
// print rules in index.css (80mm thermal roll).
function ReceiptPrintout({ sale, branch, dateStr, timeStr }) {
  const printedAt = new Date()
  return (
    <div id="receipt-print">
      <div className="rcpt-center">
        <div className="rcpt-title">{branch?.name || 'Station'}</div>
        {branch?.location ? <div className="rcpt-sub">{branch.location}</div> : null}
        <div className="rcpt-kicker" style={{ marginTop: 4 }}>CREDIT FUEL SALE RECEIPT</div>
      </div>

      <div className="rcpt-rule" />

      <PrintRow label="Receipt No" value={sale.receiptNumber} />
      <PrintRow label="Date" value={dateStr} />
      <PrintRow label="Time" value={timeStr} />

      <div className="rcpt-rule" />

      <PrintRow label="Transport Co." value={sale.companyName || '—'} />
      <PrintRow label="Driver" value={sale.driverName || '—'} />
      <PrintRow label="Plate No." value={sale.licencePlate || '—'} />
      <PrintRow label="Destination" value={sale.destination || '—'} />

      <div className="rcpt-rule" />

      <PrintRow label="Product" value={sale.productType} />
      <PrintRow label="Litres" value={`${sale.litres.toLocaleString('en-NG')} L`} />
      <PrintRow label="Unit Price" value={`${nairaPrecise(sale.unitPriceAtSale)}/L`} />

      <div className="rcpt-rule" />

      {/* Spelled out so the customer can check the arithmetic on the slip. */}
      <div className="rcpt-note rcpt-center">
        {sale.litres.toLocaleString('en-NG')} L × {nairaPrecise(sale.unitPriceAtSale)}
      </div>
      <div className="rcpt-row rcpt-total">
        <span>TOTAL</span>
        <span>{nairaPrecise(sale.totalValue)}</span>
      </div>

      <div className="rcpt-rule" />

      <PrintRow label="Status" value={STATUS_LABELS[sale.status] || String(sale.status || '').toUpperCase()} />
      {sale.notes ? <div className="rcpt-note">Notes: {sale.notes}</div> : null}
      {sale.editedAt ? <div className="rcpt-note">Edited — reason: {sale.editReason || 'not given'}</div> : null}

      <div className="rcpt-sign">
        <div className="rcpt-sign-line" />
        <div className="rcpt-sign-label">Driver signature</div>
      </div>
      <div className="rcpt-sign">
        <div className="rcpt-sign-line" />
        <div className="rcpt-sign-label">Attendant signature</div>
      </div>

      <div className="rcpt-rule" />

      <div className="rcpt-center rcpt-foot">
        <div>This is a credit sale — payment terms as agreed.</div>
        <div style={{ marginTop: 2 }}>
          Printed {printedAt.toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' })}
          {' '}
          {printedAt.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div style={{ marginTop: 4 }}>PetroTrack</div>
      </div>
    </div>
  )
}

export default function SaleReceipt({ sale, branch, onNewSale }) {
  const saleDate = new Date(sale.saleTime)
  const dateStr = saleDate.toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' })
  const timeStr = saleDate.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' })

  function handlePrint() {
    window.print()
  }

  // Room under the Print / New Sale row at the foot of this page. The bottom
  // navbar is fixed and its floating + button rises above the bar, so the
  // page's own bottom padding leaves those buttons underneath it on shorter
  // screens — and the support launcher sits in that corner, over "New Sale".
  // The desktop gap is the larger one because the page drops to `desk:pb-8`
  // there while managers keep the navbar at every width.
  return (
    <div className="space-y-4 pb-24 desk:pb-36">
      {typeof document !== 'undefined' && createPortal(
        <ReceiptPrintout sale={sale} branch={branch} dateStr={dateStr} timeStr={timeStr} />,
        document.body
      )}

      <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
          <span className="text-2xl">✓</span>
        </div>
        <p className="font-bold text-green-800 text-lg">Sale Recorded</p>
        <p className="text-xs text-green-600 font-mono mt-1">{sale.receiptNumber}</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow p-4 print:shadow-none">
        <div className="text-center mb-4 pb-3 border-b border-dashed border-gray-200">
          <p className="font-bold text-gray-800 text-base">{branch?.name}</p>
          <p className="text-xs text-gray-400 mt-0.5">CREDIT FUEL SALE RECEIPT</p>
        </div>

        <div className="divide-y divide-gray-50">
          <Row label="Receipt No" value={sale.receiptNumber} />
          <Row label="Date" value={dateStr} />
          <Row label="Time" value={timeStr} />
          <Row label="Transport Co." value={sale.companyName} />
          <Row label="Driver" value={sale.driverName} />
          <Row label="Plate No." value={sale.licencePlate || '—'} />
          <Row label="Destination" value={sale.destination} />
          <Row label="Product" value={sale.productType} />
          <Row label="Litres" value={`${sale.litres.toLocaleString('en-NG')} L`} />
          <Row label="Unit Price" value={formatNaira(sale.unitPriceAtSale)} />
          <Row label="Total Value" value={formatNaira(sale.totalValue)} bold />
        </div>

        <div className="mt-4 pt-3 border-t border-dashed border-gray-200 text-center">
          <p className="text-xs text-gray-400">This is a credit sale — payment terms as agreed</p>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={handlePrint}
          className="flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition"
        >
          <Printer className="w-4 h-4" />
          <span>Print</span>
        </button>
        <button
          onClick={onNewSale}
          className="flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition"
        >
          <Plus className="w-4 h-4" />
          <span>New Sale</span>
        </button>
      </div>
    </div>
  )
}
